/* eslint-disable @typescript-eslint/no-empty-function */
/**
 * [Sonner migration]
 *    Public API preserved so call sites do not need to change.
 *    createToast delegates to sonner's toast(); sonner manages
 *    auto-dismiss and hover-pause, so resetToastDuration /
 *    removeToastTimeout are no-ops.
 *
 *    `isToast` is observed by Modal.tsx to suppress
 *    click-outside-closes-modal while a toast is visible, so we
 *    track an active count via sonner's onDismiss / onAutoClose.
 */
import React, { useCallback, useState } from 'react';
import { toast as sonnerToast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export enum ToastTypes {
  Success,
  Fail,
}

export type IToast = {
  title: string;
  description: React.ReactNode;
  type: ToastTypes;
  duration?: number;
  id?: string;
  open?: boolean;
};

export const ToastContext = React.createContext<{
  toasts: IToast[];
  createToast: (data: IToast) => void;
  removeToast: (id: string) => void;
  resetToastDuration: (id: string, duration: number) => void;
  removeToastTimeout: (id: string) => void;
  isToast: boolean;
}>({
  toasts: [],
  createToast: () => {},
  removeToast: () => {},
  resetToastDuration: () => {},
  removeToastTimeout: () => {},
  isToast: false,
});

type Props = {
  children?: React.ReactNode;
};

const EMPTY_TOASTS: IToast[] = [];

export const ToastProvider: React.FC<Props> = ({ children }) => {
  const [activeCount, setActiveCount] = useState(0);

  const removeToast = useCallback((id: string) => {
    if (!id) return;
    sonnerToast.dismiss(id);
  }, []);

  const createToast = (toast: IToast) => {
    const id = uuidv4();
    setActiveCount(c => c + 1);
    const finish = () => setActiveCount(c => Math.max(0, c - 1));

    const baseOptions = {
      id,
      description: toast.description,
      // Original semantics: no duration → stays until manually dismissed.
      duration: toast.duration ?? Infinity,
      onDismiss: finish,
      onAutoClose: finish,
    };

    if (toast.type === ToastTypes.Success) {
      sonnerToast.success(toast.title, baseOptions);
    } else if (toast.type === ToastTypes.Fail) {
      sonnerToast.error(toast.title, baseOptions);
    }
  };

  // No-ops: sonner handles auto-dismiss + hover-pause natively.
  const resetToastDuration = useCallback((_id: string, _duration: number) => {}, []);
  const removeToastTimeout = useCallback((_id: string) => {}, []);

  return (
    <ToastContext.Provider
      value={{
        toasts: EMPTY_TOASTS,
        createToast,
        removeToast,
        resetToastDuration,
        removeToastTimeout,
        isToast: activeCount > 0,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};
